import React, { useEffect, useRef, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  KitButton,
  KitFlexCol,
  KitFlexRow,
  KitFlexRowCentered,
  KitFlexRowEnd,
  KitLink,
  KitModal,
  KitRadio,
  KitSpinner,
  KitInput,
  KitForm,
  KitUtilCommon,
  KitUtilLocale,
  KitLocalePicker,
  ThemeConstants,
} from '@chargepoint/cp-toolkit';
import Page from '../components/Page';
import { setLoginPath, setUserLogin } from '../session_storage';
import utils from '../common/utils';
import banner from '../images/web-signup-header-min.png';
import localStorage from '../localStorage';

const BannerImg = styled.img`
  max-width: 100%;
  margin-bottom: ${ThemeConstants.spacing.l}rem;
`;

const Title = styled.h1`
  text-align: center;
  font-weight: normal;
  margin-bottom: ${ThemeConstants.spacing.m}rem;
`;

const StyledForm = styled.form`
  min-width: 476px;
  @media all and (max-width: ${ThemeConstants.breakpoints.md}px) {
    min-width: 300px;
  }
`;

const CenteredDiv = styled.div`
  text-align: center;
`;

const ForgotFlexRow = styled(KitFlexRow)`
  margin-top: ${ThemeConstants.spacing.s}rem;
`;

const SubmitFlexRowCentered = styled(KitFlexRowCentered)`
  margin-top: ${ThemeConstants.spacing.xl}rem;
  margin-bottom: ${ThemeConstants.spacing.xl}rem;
`;

const SubmitButton = styled(KitButton)`
  min-width: 120px;
`;

const ValidatedUsernameDiv = styled.div`
  display: ${(props) => (props.show ? 'block' : 'none')};
  margin-bottom: ${ThemeConstants.spacing.m}rem;
`;

const SignupLine = styled.div`
  margin-top: ${ThemeConstants.spacing.m}rem;
`;

const SignInState = {
  username_validation: 0,
  username_validating: 1,
  username_validation_error: 2,
  username_validation_success: 3,
  password_validating: 4,
  password_validation_error: 5,
  password_validation_success: 6,
  select_usernames: 7,
};
Object.freeze(SignInState);

function SignIn() {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const usernameQueryParam = KitUtilCommon.getQueryParam(window.location.search, 'username');
  const [username, setUsername] = useState(usernameQueryParam ? usernameQueryParam : '');
  const [password, setPassword] = useState('');
  const [selectUsernames, setSelectUsernames] = useState([]);
  const [selectedUsernameIndex, setSelectedUsernameIndex] = useState(0);
  const [signInState, setSignInState] = useState(SignInState.username_validation);
  const [lastErrorMessage, setLastErrorMessage] = useState('');
  const [localePickerResult, setLocalePickerResult] = useState(null);
  const usernameRef = useRef(null);
  const passwordRef = useRef(null);
  const { search } = useLocation()

  useEffect(() => {
    const subdomain = utils.getSubdomain();
    const regionCode = KitUtilLocale.getRegionCodeForSSOSubdomain(t, subdomain);
    const defaultLocaleCode = KitUtilLocale.getDefaultLocaleCodeForRegionCode(t, regionCode);
    const localeCode = localStorage.getLocaleCode(defaultLocaleCode);
    setLocalePickerResult(KitLocalePicker.getKitLocalePickerResult(t, regionCode, localeCode));
  }, [t, setLocalePickerResult]);

  useEffect(() => {
    let loginPath = window.location.pathname;
    const redirectQueryParam = KitUtilCommon.getQueryParam(window.location.search, 'redirect');
    if (redirectQueryParam) {
      loginPath += KitUtilCommon.getQueryString({
        redirect: redirectQueryParam,
      });
    }
    setLoginPath(loginPath);
  }, []);

  useEffect(() => {
    if (signInState === SignInState.username_validation) {
      // Initial state should set focus to username input
      usernameRef.current.focus();
    }
    if (signInState === SignInState.username_validation_success) {
      // Username is validated... set focus to password input
      passwordRef.current.focus();
    }
  }, [signInState]);

  const changeUsernameClicked = () => {
    setSignInState(SignInState.username_validation);
  };

  const displayUnknownError = () => {
    const unknown = t('error_ids.unknown');
    setLastErrorMessage(
      t('error_with_message', {
        message: unknown,
      })
    );
  };

  const checkForAlreadyLoggedInUser = async () => {
    try {
      const requestHeaders = utils.getCommonRequestHeaders(i18n);

      let apiUrl = utils.makeSsoApiUrl('/v1/user/current');
      const redirectQueryParam = KitUtilCommon.getQueryParam(window.location.search, 'redirect');
      if (redirectQueryParam) {
        apiUrl += `?redirect=${encodeURIComponent(redirectQueryParam)}`;
      }

      const response = await fetch(apiUrl, {
        method: 'GET',
        credentials: 'include',
        headers: requestHeaders,
      });
      const data = await response.json();

      const { hasOwnProperty } = Object.prototype;

      switch (response.status) {
        case 200:
          if (hasOwnProperty.call(data, 'challenge_response')) {
            const { challenge_response: challengeResponse } = data;
            if (challengeResponse === 'LANDING_PAGE_REDIRECT') {
              window.location = data.redirect_url;
            }
          }
          break;
        default:
          // Just drop to the SSO login
          break;
      }
    } catch (err) {
      console.error(`Unknown Error: ${err}`);
    }
  };

  const validateUsername = async () => {
    try {
      const requestHeaders = utils.getCommonRequestHeaders(i18n);

      const requestBody = {
        username,
        timezone_offset: new Date().getTimezoneOffset(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      const redirectQueryParam = KitUtilCommon.getQueryParam(window.location.search, 'redirect');
      if (redirectQueryParam) {
        requestBody.redirect = redirectQueryParam;
      }

      const apiUrl = utils.makeSsoApiUrl('/v1/user/login');
      const response = await fetch(apiUrl, {
        method: 'POST',
        credentials: 'include',
        headers: requestHeaders,
        body: JSON.stringify(requestBody),
      });
      const data = await response.json();

      const { hasOwnProperty } = Object.prototype;

      switch (response.status) {
        case 200:
          if (hasOwnProperty.call(data, 'challenge_response')) {
            setUserLogin(username);
            const { challenge_response: challengeResponse } = data;
            if (challengeResponse === 'PASSWORD') {
              setSignInState(SignInState.username_validation_success);
            } else if (
              challengeResponse === 'IDP_LOGIN' ||
              challengeResponse === 'NOSLET_REDIRECT'
            ) {
              window.location = data.redirect_url;
            } else {
              displayUnknownError();
              console.error(`Unexpected challenge response: ${challengeResponse}`);
              setSignInState(SignInState.username_validation_error);
            }
          } else {
            setLastErrorMessage('Error validating user: Missing challenge response');
            setSignInState(SignInState.username_validation_error);
          }
          break;
        default:
          if (hasOwnProperty.call(data, 'error_message')) {
            // Returned error_message will be localized by SSO backend
            setLastErrorMessage(data.error_message);
          } else {
            setLastErrorMessage(
              t('error_with_message', {
                message: `[${response.status}] ${response.statusText}`,
              })
            );
          }
          setSignInState(SignInState.username_validation_error);
          break;
      }
    } catch (err) {
      displayUnknownError();
      console.error(`Unknown Error: ${err}`);
      setSignInState(SignInState.username_validation_error);
    }
  };

  useEffect(() => {
    async function performStartupChecks() {
      await checkForAlreadyLoggedInUser();

      if (usernameQueryParam) {
        // This will skip directly to password entry as the username query param is present.
        // IMPORTANT: Needs to call validateUsername in the edge case this user account is in a separate region/noslet.
        // See NOSLET_REDIRECT challenge response!!!
        setSignInState(SignInState.username_validating);
        await validateUsername();
      }
    }

    performStartupChecks();
  }, []);

  const attemptLogin = async () => {
    try {
      const requestHeaders = utils.getCommonRequestHeaders(i18n);

      const requestBody = {
        username,
        password,
        timezone_offset: new Date().getTimezoneOffset(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      const redirectQueryParam = KitUtilCommon.getQueryParam(window.location.search, 'redirect');
      if (redirectQueryParam) {
        requestBody.redirect = redirectQueryParam;
      }

      const apiUrl = utils.makeSsoApiUrl('/v1/user/login');
      const response = await fetch(apiUrl, {
        method: 'POST',
        credentials: 'include',
        headers: requestHeaders,
        body: JSON.stringify(requestBody),
      });
      const data = await response.json();

      const { hasOwnProperty } = Object.prototype;

      switch (response.status) {
        case 200:
          if (hasOwnProperty.call(data, 'challenge_response')) {
            const { challenge_response: challengeResponse } = data;
            if (challengeResponse === 'SELECT_USERNAMES') {
              const { data: usernamesData } = data;
              if (usernamesData.length > 0) {
                setSelectUsernames(usernamesData);
                setUsername(usernamesData[0]);
                setSignInState(SignInState.select_usernames);
              } else {
                displayUnknownError();
                console.error('SELECT_USERNAMES challenge missing usernames');
                setSignInState(SignInState.username_validation_error);
              }
            } else if (challengeResponse === 'NOSLET_REDIRECT') {
              window.location = data.redirect_url;
            } else if (challengeResponse === 'LANDING_PAGE_REDIRECT') {
              if (data.redirect_url.indexOf(window.location.origin) !== -1) {
                history.push(data.redirect_url.replace(window.location.origin, ''));
              } else {
                window.location = data.redirect_url;
              }
            } else {
              displayUnknownError();
              console.error(`Unexpected challenge response: ${challengeResponse}`);
              setSignInState(SignInState.username_validation_error);
            }
          } else if (hasOwnProperty.call(data, 'redirect_url')) {
            setSignInState(SignInState.password_validation_success);
            window.location = data.redirect_url;
          } else {
            setLastErrorMessage('Missing redirect_url');
            setSignInState(SignInState.password_validation_error);
          }
          break;
        default:
          if (hasOwnProperty.call(data, 'error_message')) {
            // Returned error_message will be localized by SSO backend
            setLastErrorMessage(data.error_message);
          } else {
            setLastErrorMessage(
              t('error_with_message', {
                message: `[${response.status}] ${response.statusText}`,
              })
            );
          }
          setSignInState(SignInState.password_validation_error);
          break;
      }
    } catch (err) {
      displayUnknownError();
      console.error(`Unknown Error: ${err}`);
      setSignInState(SignInState.password_validation_error);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    switch (signInState) {
      case SignInState.username_validation:
      case SignInState.username_validation_error:
        setSignInState(SignInState.username_validating);
        validateUsername();
        break;
      case SignInState.username_validation_success:
      case SignInState.password_validation_error:
        setSignInState(SignInState.password_validating);
        attemptLogin();
        break;
      default:
        break;
    }
  };

  const [signupText, signupLink] = utils.getSignupLinkWithText(search, localePickerResult)

  let buttonDisabled = false;
  let buttonTitle = t('next');
  if (signInState >= SignInState.username_validation_success) {
    buttonTitle = t('login_button');
  }

  if (
    signInState === SignInState.username_validating ||
    signInState === SignInState.password_validating
  ) {
    buttonDisabled = true;
    buttonTitle = <KitSpinner size='s' t={t} />;
  }

  const forgotUsernamePath = '/forgot-username';

  let forgotPasswordPath = '/forgot-password';
  forgotPasswordPath += KitUtilCommon.getQueryString({
    username,
  });

  let usernameError;
  if (signInState === SignInState.username_validation_error) {
    usernameError = lastErrorMessage;
  }

  let passwordError;
  if (signInState === SignInState.password_validation_error) {
    passwordError = lastErrorMessage;
  }

  let selectUsernameModal;
  if (signInState === SignInState.select_usernames) {
    const radios = [];

    const onSelectUsername = (i) => {
      setSelectedUsernameIndex(i);
      setUsername(selectUsernames[i]);
    };

    for (let i = 0; i < selectUsernames.length; i += 1) {
      const uname = selectUsernames[i];
      const radioId = `radio_${uname}`;
      radios.push(
        <KitRadio
          key={uname}
          data-qa-id={radioId}
          checked={selectedUsernameIndex === i}
          name='username'
          label={uname}
          value={uname}
          onChange={() => onSelectUsername(i)}
        />
      );
    }

    const onContinue = () => {
      setSelectedUsernameIndex(0);
      setSignInState(SignInState.password_validating);
      attemptLogin();
    };

    selectUsernameModal = (
      <KitModal show closeOnClickOutside={false} position='center'>
        <KitModal.Header t={t}>
          <KitModal.Title>{t('multiple_usernames_found')}</KitModal.Title>
        </KitModal.Header>
        <KitModal.Body>
          <p>{t('select_username_to_continue_logging_in')}</p>
          <div>{radios}</div>
        </KitModal.Body>
        <KitModal.Footer>
          <KitFlexRowEnd>
            <KitButton data-qa-id='select_username_continue' onClick={onContinue}>
              {t('continue')}
            </KitButton>
          </KitFlexRowEnd>
        </KitModal.Footer>
      </KitModal>
    );
  }

  return (
    <Page title={t('login_title')}>
      <KitFlexCol>
        <KitFlexRowCentered>
          <BannerImg src={banner} alt='ChargePoint' />
        </KitFlexRowCentered>
        <Title>{t('login_title')}</Title>
        <KitFlexRowCentered>
          <StyledForm onSubmit={handleSubmit} noValidate>
            <ValidatedUsernameDiv
              show={signInState >= SignInState.username_validation_success ? 1 : 0}
            >
              <CenteredDiv>{username}</CenteredDiv>
              <CenteredDiv>
                <KitLink data-qa-id='change_username_link' onClick={changeUsernameClicked}>
                  {t('change')}
                </KitLink>
              </CenteredDiv>
            </ValidatedUsernameDiv>
            {signInState < SignInState.username_validation_success && (
              <>
                <KitForm.Label text={t('email_or_username_hint')} htmlFor='username' />
                <KitInput
                  id='username'
                  data-qa-id='username'
                  name='username'
                  ref={usernameRef}
                  aria-label={t('email_or_username_hint')}
                  type='text'
                  disabled={signInState === SignInState.username_validating}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  isError={!!usernameError}
                />
                <KitForm.InfoText
                  text={usernameError}
                  isError={!!usernameError}
                  id='usernameError'
                />
                <ForgotFlexRow>
                  <Link data-qa-id='forgot_username_link' to={forgotUsernamePath}>
                    {t('forgot_username')}
                  </Link>
                </ForgotFlexRow>
              </>
            )}
            {signInState >= SignInState.username_validation_success && (
              <>
                <KitForm.Label text={t('password_hint')} htmlFor='password' />
                <KitInput
                  id='password'
                  data-qa-id='password'
                  name='password'
                  ref={passwordRef}
                  aria-label={t('password_hint')}
                  type='password'
                  disabled={signInState === SignInState.username_validating}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  isError={!!passwordError}
                />
                <KitForm.InfoText
                  text={passwordError}
                  isError={!!passwordError}
                  id='passwordError'
                />
                <ForgotFlexRow>
                  <Link data-qa-id='forgot_password_link' to={forgotPasswordPath}>
                    {t('forgot_password')}
                  </Link>
                </ForgotFlexRow>
              </>
            )}
            <SubmitFlexRowCentered>
              <SubmitButton
                data-qa-id='form_submit_button'
                variant='primary'
                type='submit'
                disabled={buttonDisabled}
              >
                {buttonTitle}
              </SubmitButton>
            </SubmitFlexRowCentered>
            <KitFlexRowCentered>
              <SignupLine>
                {signupText}{' '}
                <KitLink data-qa-id='sign_up_link' href={signupLink}>
                  {t('sign_up')}
                </KitLink>
              </SignupLine>
            </KitFlexRowCentered>
          </StyledForm>
        </KitFlexRowCentered>
      </KitFlexCol>
      {selectUsernameModal}
    </Page>
  );
}

export default SignIn;
